import { HttpClient, HttpParams } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { enpointCervinoWeb } from "src/app/configuration/enpointsCervino";
import { MaintenanceSchedulingModel } from "src/app/models/maintenance/maintenance-scheduling.model";
import { ColumnResponseModel } from "src/app/models/responses/column-response.model";
import { PagedResponseModel } from "src/app/models/responses/paged-response.model";
import { MaintenanceSchedulingTableModel } from "src/app/models/tables/maintenance/maintenance-scheduling-table.model";
import { environment } from "src/environments/environment";

@Injectable({
  providedIn: 'root'
})
export class MaintenanceSchedulingService {
  private readonly _httpClient = inject(HttpClient);

  createMaintenanceScheduling(maintenanceSchedulingModel: MaintenanceSchedulingModel): Observable<MaintenanceSchedulingModel> {
    return this._httpClient.post<MaintenanceSchedulingModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.maintenanceScheduling.CreateAsync}`,
      maintenanceSchedulingModel);
  }

  updateMaintenanceScheduling(maintenanceSchedulingModel: MaintenanceSchedulingModel): Observable<MaintenanceSchedulingModel> {
    return this._httpClient.put<MaintenanceSchedulingModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.maintenanceScheduling.UpdateAsync}`,
      maintenanceSchedulingModel
    );
  }

  deleteMaintenanceScheduling(maintenanceSchedulingId: number): Observable<MaintenanceSchedulingModel> {
    const params = new HttpParams().set('maintenanceSchedulingId', maintenanceSchedulingId);
    return this._httpClient.delete<MaintenanceSchedulingModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.maintenanceScheduling.DeleteAsync}`,
      { params }
    );
  }

  getMaintenanceSchedulingById(maintenanceSchedulingId: number): Observable<MaintenanceSchedulingModel> {
    const params = new HttpParams().set('maintenanceSchedulingId', maintenanceSchedulingId);
    return this._httpClient.get<MaintenanceSchedulingModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.maintenanceScheduling.GetByIdAsync}`,
      { params }
    );
  }

  getAllMaintenanceSchedulingWithPagination(page: number = 1, limit: number = 10, columnSort: string | null = null, ascendant: boolean = false, filters: ColumnResponseModel[]): Observable<PagedResponseModel<MaintenanceSchedulingTableModel>> {
    const params = new HttpParams().set('page', page).set('limit', limit).set('columnSort', columnSort || '').set('ascendant', ascendant);
    return this._httpClient.post<PagedResponseModel<MaintenanceSchedulingTableModel>>(
      `${environment.apiCervinoBackend}${enpointCervinoWeb.maintenanceScheduling.GetAllMaintenanceSchedulingWithPaginationAsync}`, filters,
      { params }
    );
  }

  exportGeneralConsultation(filters: ColumnResponseModel[], columnSort: string | null, ascendant: boolean): Observable<Blob> {
    const params = new HttpParams().set('columnSort', columnSort ?? '').set('ascendant', ascendant.toString());
    return this._httpClient.post(`${environment.apiCervinoBackend}${enpointCervinoWeb.maintenanceScheduling.ExportGeneralConsultation}`, filters, {
      params,
      responseType: 'blob',
    });
  }

  getColumnsForGeneralConsultation(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(
      `${environment.apiCervinoBackend}${enpointCervinoWeb.maintenanceScheduling.GetColumnsForGeneralConsultationAsync}`
    );
  }

  getFirstMaintenanceSchedulingByKeys(branchId: number): Observable<MaintenanceSchedulingModel> {
    const params = new HttpParams().set('branchId', branchId);
    return this._httpClient.get<MaintenanceSchedulingModel>(
      `${environment.apiCervinoBackend}${enpointCervinoWeb.maintenanceScheduling.GetFirstMaintenanceSchedulingByKeysAsync}`,
      { params }
    );
  }

  getLastMaintenanceSchedulingByKeys(branchId: number): Observable<MaintenanceSchedulingModel> {
    const params = new HttpParams().set('branchId', branchId);
    return this._httpClient.get<MaintenanceSchedulingModel>(
      `${environment.apiCervinoBackend}${enpointCervinoWeb.maintenanceScheduling.GetLastMaintenanceSchedulingByKeysAsync}`,
      { params }
    );
  }

  getLastMaintenanceSchedulingByType(branchId: number, typeMaintenanceScheduling: number): Observable<MaintenanceSchedulingModel> {
    const params = new HttpParams().set('branchId', branchId).set('typeMaintenanceScheduling', typeMaintenanceScheduling);
    return this._httpClient.get<MaintenanceSchedulingModel>(
      `${environment.apiCervinoBackend}${enpointCervinoWeb.maintenanceScheduling.GetLastMaintenanceSchedulingByTypeAsync}`,
      { params }
    );
  }

  getMaintenanceSchedulingByKeys(branchId: number, consecutive: number): Observable<MaintenanceSchedulingModel> {
    const params = new HttpParams().set('branchId', branchId).set('consecutive', consecutive);

    return this._httpClient.get<MaintenanceSchedulingModel>(
      `${environment.apiCervinoBackend}${enpointCervinoWeb.maintenanceScheduling.GetMaintenanceSchedulingByKeysAsync}`,
      { params }
    );
  }

  getNextMaintenanceSchedulingByKeys(branchId: number, consecutive: number): Observable<MaintenanceSchedulingModel> {
    const params = new HttpParams().set('branchId', branchId).set('consecutive', consecutive);
    return this._httpClient.get<MaintenanceSchedulingModel>(
      `${environment.apiCervinoBackend}${enpointCervinoWeb.maintenanceScheduling.GetNextMaintenanceSchedulingByKeysAsync}`,
      { params }
    );
  }

  getPreviousMaintenanceSchedulingByKeys(branchId: number, consecutive: number): Observable<MaintenanceSchedulingModel> {
    const params = new HttpParams().set('branchId', branchId).set('consecutive', consecutive);
    return this._httpClient.get<MaintenanceSchedulingModel>(
      `${environment.apiCervinoBackend}${enpointCervinoWeb.maintenanceScheduling.GetPreviousMaintenanceSchedulingByKeysAsync}`,
      { params }
    );
  }

  setActiveMaintenanceScheduling(maintenanceSchedulingId: number): Observable<MaintenanceSchedulingModel> {
    return this._httpClient.put<MaintenanceSchedulingModel>(
      `${environment.apiCervinoBackend}${enpointCervinoWeb.maintenanceScheduling.SetActiveAsync}/${maintenanceSchedulingId}`,
      {}
    );
  }

  getPermission(permission: number): Observable<boolean> {
    const params = new HttpParams().set('permission', permission);
    return this._httpClient.get<boolean>(
      `${environment.apiCervinoBackend}${enpointCervinoWeb.maintenanceScheduling.GetPermissionAsync}`,
      { params }
    );
  }

  getTotalWithoutMaintenanceOrder(maintenanceSchedulingId: number): Observable<number> {
    return this._httpClient.get<number>(
      `${environment.apiCervinoBackend}${enpointCervinoWeb.maintenanceScheduling.GetTotalWithoutMaintenanceOrder}/${maintenanceSchedulingId}`
    );
  }
}
